import { ActionIcon, Badge, Group, Popover, ScrollArea, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFileSpreadsheet, IconHelpCircle, IconMinus, IconPlus, IconWorldWww, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CsvImportModal } from './CsvImportModal';
import { dedupeByCanonical } from './csvUrlParser';

interface UrlPatternInputProps {
  label: string;
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  minRows?: number; // 1 for include, 0 for exclude
  variant?: 'include' | 'exclude';
  error?: string;
}

export const UrlPatternInput = ({
  label,
  value,
  onChange,
  placeholder,
  minRows = 1,
  variant = 'include',
  error,
}: UrlPatternInputProps) => {
  const { t } = useTranslation();
  const [importOpened, { open: openImport, close: closeImport }] = useDisclosure(false);

  const addRow = () => {
    onChange([...value, '']);
  };

  const removeRow = (index: number) => {
    if (value.length <= minRows) return;
    const next = value.filter((_, i) => i !== index);
    onChange(next.length === 0 ? [] : next);
  };

  const updateRow = (index: number, val: string) => {
    const next = [...value];
    next[index] = val;
    onChange(next);
  };

  const isExclude = variant === 'exclude';
  const resolvedPlaceholder =
    placeholder ?? (isExclude ? 'https://example.com/admin/*' : t('create_v2.website.url_placeholder'));

  // 超过此数量时启用滚动区域
  const SCROLL_THRESHOLD = 6;
  const nonEmptyCount = value.filter((v) => v.trim()).length;
  const needsScroll = value.length > SCROLL_THRESHOLD;

  // 除外URL 模式下，如果为空数组，显示添加按钮
  if (isExclude && value.length === 0) {
    return (
      <div>
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
        <ActionIcon variant="light" color="gray" onClick={addRow} size="sm">
          <IconPlus size={14} />
        </ActionIcon>
      </div>
    );
  }

  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Group gap={4}>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {nonEmptyCount > 1 && (
            <Badge size="sm" variant="light" color="gray">
              {nonEmptyCount}
            </Badge>
          )}
          {!isExclude && <WildcardHelpPopover />}
          {!isExclude && (
            <UnstyledButton onClick={openImport} ml={4}>
              <Group gap={2}>
                <IconFileSpreadsheet size={14} color="var(--mantine-color-blue-6)" />
                <Text size="xs" c="blue.6">
                  {t('create_v2.website.csv_import.button')}
                </Text>
              </Group>
            </UnstyledButton>
          )}
        </Group>
      </Group>

      <ScrollArea.Autosize mah={needsScroll ? 280 : undefined}>
        <Stack gap="xs">
          {value.map((v, index) => (
            <Group key={index} gap="xs" wrap="nowrap">
              <TextInput
                className="flex-1"
                placeholder={resolvedPlaceholder}
                leftSection={<IconWorldWww size={16} />}
                rightSection={
                  v ? (
                    <ActionIcon variant="transparent" color="gray" size="sm" onClick={() => updateRow(index, '')}>
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                value={v}
                onChange={(e) => updateRow(index, e.currentTarget.value)}
                error={index === 0 && error ? error : undefined}
              />
              <ActionIcon variant="light" color="blue" onClick={addRow} size="sm">
                <IconPlus size={14} />
              </ActionIcon>
              <ActionIcon
                variant="light"
                color={isExclude ? 'red' : 'gray'}
                onClick={() => removeRow(index)}
                size="sm"
                disabled={value.length <= minRows}
              >
                <IconMinus size={14} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      </ScrollArea.Autosize>
      {!isExclude && (
        <Text size="xs" c="dimmed" mt={4}>
          {t('create_v2.website.url_hint')}
        </Text>
      )}

      {!isExclude && (
        <CsvImportModal
          opened={importOpened}
          onClose={closeImport}
          existingUrls={value}
          onConfirm={(importedUrls) => {
            const existingNonEmpty = value.filter((v) => v.trim());
            // 按 canonical 形式去重（与后端一致），保留首次出现的原始 URL
            const { unique } = dedupeByCanonical([...existingNonEmpty, ...importedUrls]);
            onChange(unique.length > 0 ? unique : ['']);
            closeImport();
          }}
        />
      )}
    </div>
  );
};

/** 通配符帮助面板 */
const WildcardHelpPopover = () => {
  const { t } = useTranslation();

  return (
    <Popover width={400} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <UnstyledButton>
          <Group gap={2}>
            <IconHelpCircle size={14} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="blue.6" style={{ cursor: 'pointer' }}>
              {t('create_v2.website.wildcard_help.link_text')}
            </Text>
          </Group>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            {t('create_v2.website.wildcard_help.title')}
          </Text>
          <Text size="xs" c="gray.7">
            {t('create_v2.website.wildcard_help.description')}
          </Text>
          <div>
            <Text size="xs" fw={500} mb={4}>
              {t('create_v2.website.wildcard_help.basic_title')}
            </Text>
            <Text size="xs" c="gray.7" component="div">
              <div>
                <code>*</code> — {t('create_v2.website.wildcard_help.single_star')}
              </div>
              <div>
                <code>**</code> — {t('create_v2.website.wildcard_help.double_star')}
              </div>
            </Text>
          </div>
          <div>
            <Text size="xs" fw={500} mb={4}>
              {t('create_v2.website.wildcard_help.examples_title')}
            </Text>
            <Text size="xs" c="gray.7" component="div" style={{ whiteSpace: 'pre-line' }}>
              {`https://example.com/blog/*\n→ ${t(
                'create_v2.website.wildcard_help.example1'
              )}\n\nhttps://example.com/blog/**\n→ ${t(
                'create_v2.website.wildcard_help.example2'
              )}\n\nhttps://example.com/docs/*/guide\n→ ${t('create_v2.website.wildcard_help.example3')}`}
            </Text>
          </div>
          <Text size="xs" c="gray.6">
            {t('create_v2.website.wildcard_help.notes')}
          </Text>
          <Text size="xs" c="blue.6" fw={500}>
            {t('create_v2.website.wildcard_help.tip')}
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
